/**
 * Most-recently-used ranking for Ledger Entry autocomplete suggestions.
 * See documentation/Ledger_MRU_plan.md
 *
 * Storage: localStorage key pixie-autocomplete-mru:v1:{userId|anon}
 */

export const MRU_CATEGORIES = {
  NAME: 'ledger.name',
  MEMO: 'ledger.memo',
  COA: 'ledger.coa', // Account + Split share this
  CLASS: 'ledger.class',
};

const STORAGE_PREFIX = 'pixie-autocomplete-mru:v1:';
const DEFAULT_CAP = 75;

/** @type {string|number|null} */
let currentUserId = null;

/** In-memory cache of the full MRU map for the current user */
let cache = null;
let cacheUserKey = null;

/**
 * Scope MRU lists to a user (call on LedgerEntry mount).
 * @param {string|number|null|undefined} userId
 */
export function setMruUserId(userId) {
  const next =
    userId != null && userId !== '' ? String(userId) : null;
  if (next !== currentUserId) {
    currentUserId = next;
    cache = null;
    cacheUserKey = null;
  }
}

/**
 * Map LedgerEntry field name → MRU category.
 * @param {string} field
 * @returns {string|null}
 */
export function fieldToCategory(field) {
  switch (field) {
    case 'Name':
      return MRU_CATEGORIES.NAME;
    case 'Memo':
      return MRU_CATEGORIES.MEMO;
    case 'Account':
    case 'Split':
      return MRU_CATEGORIES.COA;
    case 'Class':
      return MRU_CATEGORIES.CLASS;
    default:
      return null;
  }
}

function storageKey() {
  return `${STORAGE_PREFIX}${currentUserId != null ? currentUserId : 'anon'}`;
}

function loadStore() {
  const key = storageKey();
  if (cache && cacheUserKey === key) return cache;
  try {
    const raw = localStorage.getItem(key);
    cache = raw ? JSON.parse(raw) : {};
    if (!cache || typeof cache !== 'object') cache = {};
  } catch {
    cache = {};
  }
  cacheUserKey = key;
  return cache;
}

function saveStore(store) {
  cache = store;
  cacheUserKey = storageKey();
  try {
    localStorage.setItem(cacheUserKey, JSON.stringify(store));
  } catch {
    /* quota / private mode */
  }
}

/**
 * @param {string} category
 * @returns {string[]}
 */
export function getMruList(category) {
  if (!category) return [];
  const store = loadStore();
  const list = store[category];
  return Array.isArray(list) ? list.slice() : [];
}

/**
 * Record that the user chose `value` for `category` (most recent first).
 * @param {string} category
 * @param {string} value
 * @param {{ cap?: number }} [options]
 */
export function recordUse(category, value, options = {}) {
  if (!category) return;
  const v = String(value ?? '').trim();
  if (!v) return;

  const cap = options.cap ?? DEFAULT_CAP;
  const store = loadStore();
  const prev = Array.isArray(store[category]) ? store[category] : [];
  const lower = v.toLowerCase();
  const next = [
    v,
    ...prev.filter((item) => String(item).trim().toLowerCase() !== lower),
  ].slice(0, cap);

  store[category] = next;
  saveStore(store);
}

/**
 * Record use for a LedgerEntry field name.
 * @param {string} field
 * @param {string} value
 */
export function recordFieldUse(field, value) {
  const cat = fieldToCategory(field);
  if (cat) recordUse(cat, value);
}

/**
 * Reorder filtered match items so MRU values come first.
 * @template T
 * @param {string} category
 * @param {T[]} items
 * @param {{
 *   getValue?: (item: T) => string,
 *   secondaryCompare?: (a: string, b: string) => number,
 * }} [options]
 * @returns {T[]}
 */
export function rankMatches(category, items, options = {}) {
  if (!Array.isArray(items) || items.length <= 1) return items;

  const getValue =
    options.getValue ||
    ((item) => {
      if (item == null) return '';
      if (typeof item === 'string') return item;
      if (typeof item.value === 'string') return item.value;
      return String(item);
    });

  const secondaryCompare =
    options.secondaryCompare ||
    ((a, b) => String(a).localeCompare(String(b)));

  const mru = getMruList(category);
  const mruIndex = new Map();
  mru.forEach((v, i) => {
    const k = String(v).trim().toLowerCase();
    if (k && !mruIndex.has(k)) mruIndex.set(k, i);
  });

  return items
    .map((item, origIndex) => {
      const value = getValue(item) || '';
      const key = value.trim().toLowerCase();
      const rank = mruIndex.has(key) ? mruIndex.get(key) : Number.POSITIVE_INFINITY;
      return { item, origIndex, value, rank };
    })
    .sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      const sec = secondaryCompare(a.value, b.value);
      if (sec !== 0) return sec;
      return a.origIndex - b.origIndex;
    })
    .map(({ item }) => item);
}

/**
 * Class secondary sort: no colon before colon, then alpha.
 * @param {string} a
 * @param {string} b
 */
export function classSecondaryCompare(a, b) {
  const aColon = String(a).includes(':') ? 1 : 0;
  const bColon = String(b).includes(':') ? 1 : 0;
  if (aColon !== bColon) return aColon - bColon;
  return String(a).localeCompare(String(b));
}

/**
 * @param {string} [category] omit to clear all categories for this user
 */
export function clearMru(category) {
  const store = loadStore();
  if (category) {
    delete store[category];
  } else {
    Object.keys(store).forEach((k) => delete store[k]);
  }
  saveStore(store);
}

/**
 * Best-effort user id from JWT in localStorage (for storage scoping).
 * @returns {string|null}
 */
export function resolveUserIdFromToken() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    );
    const id = payload.sub ?? payload.userId ?? payload.id;
    return id != null && id !== '' ? String(id) : null;
  } catch {
    return null;
  }
}
